import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

const getColor = (health) => {
  if (health >= 85) return 'bg-green-500';
  if (health >= 70) return 'bg-yellow-400';
  return 'bg-red-500';
};

const HealthBar = ({ health, average }) => {
  const barRef = useRef();
  const containerRef = useRef();
  const [barWidth, setBarWidth] = useState(0);

  let healthColor = "#E5484D";
  if (health >= 50) healthColor = '#FFC53D';
  if (health >= 80) healthColor = '#30A46C';

  // Observe container width
  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      const { width } = entries[0].contentRect;
      setBarWidth(width);
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // D3 rendering
  useEffect(() => {
    if (barWidth === 0) return;

    const height = 20;

    const svg = d3.select(barRef.current)
      .attr('width', barWidth - 10)
      .attr('height', height);

    svg.selectAll('*').remove();

    svg.append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', barWidth)
      .attr('height', height)
      .attr('fill', '#e5e7eb');

    svg.append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', (health / 100) * barWidth)
      .attr('height', height)
      .attr('fill', healthColor);

    svg.append('line')
      .attr('x1', (average / 100) * barWidth)
      .attr('x2', (average / 100) * barWidth)
      .attr('y1', 0)
      .attr('y2', height)
      .attr('stroke', '#032FCE')
      .attr('stroke-width', 2);

  }, [barWidth, health, average]);

  return (
    <div ref={containerRef} className="w-full">
      <svg ref={barRef}></svg>
    </div>
  );
};

const ComponentsHealth = ({ data = [] }) => {

  const [componentData, setTableData] = useState(data);

  useEffect(() => {
    setTableData(data);
  }, [data]);

  const getIconForId = (id) => {
    switch (id) {
      case 3:
        return <i className="ti ti-tool" />;
      case 4:
        return <i className="ti ti-manual-gearbox" />;
      case 5:
        return <i className="ti ti-gauge" />;
      case 6:
        return <i className="ti ti-plug-connected" />;
      case 7:
        return <i className="ti ti-truck" />;
      case 8:
        return <i className="ti ti-shield-check" />;
      default:
        return null; // Render nothing for unmatched IDs
    }
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-semibold mb-4">Component Health</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {componentData.map((system) => (
          <div key={system.id} className="bg-white p-4 rounded shadow">
            <h3 className="font-bold text-md mb-4">{system.name}</h3>
            {system.subsystems.map((sub) => (
              <div key={sub.id} className="mb-4 py-1">
                <h4 className="font-semibold text-gray-800 mb-2">
                  {getIconForId(sub.id)}  
                  {sub.name}
                </h4>
                {sub.components.map((comp) => (
                  <div key={comp.id} className="mb-2 py-1">
                    <div className="flex items-center">
                      {/* Left section: Icon and Component Name (40%) */}
                      <div className="w-[40%] flex items-center space-x-2">
                        <i
                          className={`ti ti-circle-filled font-bold text-xl ${
                            comp.health >= 80
                              ? 'c_green'
                              : comp.health >= 50
                                ? 'c_yellow'
                                : 'c_red'
                          }`}
                        />
                        <span className="text-gray-700 text-sm font-medium">{comp.name}</span>
                      </div>

                      {/* Right section: Health Bar (60%) */}
                      <div className="w-[58%] flex items-center">
                        <span
                          className={`text-xs font-bold ${
                            comp.health >= 80
                              ? 'c_green'
                              : comp.health >= 50
                                ? 'c_dark_yellow'
                                : 'c_red'
                          }`}
                        >
                          {comp.health}%
                        </span>
                        <div className="flex-1 ml-1">
                          <HealthBar health={comp.health} average={comp.average} />
                        </div>
                        <span className="text-xs ml-0 c_dark_blue font-bold">{comp.average}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ComponentsHealth;